import React, { CSSProperties, useEffect, useState } from "react";
import axios from "axios";
import { theme } from "../theme";
import { BASE_URL } from "../config";
import type { GenericResponse } from "@away-moe/shared";

type FeedbackModalProps = {
  onClose: () => void;
};

type Status = "idle" | "sent" | "error";

const FeedbackModal = ({ onClose }: FeedbackModalProps) => {
  const [message, setMessage] = useState("");
  const [sender, setSender] = useState("");
  const [status, setStatus] = useState<Status>("idle");

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim() || status === "sent") return;
    setStatus("sent");
    setTimeout(onClose, 1200);
    try {
      await axios.post<GenericResponse>(`${BASE_URL}/api/send_mail/`, {
        message: message.trim(),
        sender: sender.trim() || null,
      });
    } catch {
      setStatus("error");
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div
        style={styles.modal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Send feedback"
      >
        <button style={styles.close} onClick={onClose} aria-label="Close">
          ×
        </button>
        <h2 style={styles.title}>Feedback</h2>
        <p style={styles.description}>
          Send me any suggestions or improvements for this site, or really just whatever you
          want
        </p>
        <form onSubmit={handleSubmit} style={styles.form}>
          <textarea
            style={styles.textarea}
            placeholder="Your message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            autoFocus
            rows={5}
          />
          <input
            style={styles.input}
            type="text"
            placeholder="Who are you (optional)"
            value={sender}
            onChange={(e) => setSender(e.target.value)}
          />
          {status === "error" && (
            <span style={styles.error}>Something went wrong. Try again.</span>
          )}
          <button
            type="submit"
            style={{
              ...styles.send,
              ...(!message.trim() || status === "sent"
                ? styles.sendDisabled
                : {}),
            }}
            disabled={!message.trim() || status === "sent"}
          >
            {status === "sent" ? "Sent!" : "Send"}
          </button>
        </form>
      </div>
    </div>
  );
};

const styles: Record<string, CSSProperties> = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.35)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "16px",
    zIndex: 1000,
  },
  modal: {
    position: "relative",
    background: theme.color.surface,
    borderRadius: theme.radius.lg,
    boxShadow: theme.shadow.card,
    border: `1px solid ${theme.color.border}`,
    padding: "28px",
    width: "100%",
    maxWidth: "420px",
  },
  close: {
    position: "absolute",
    top: "12px",
    right: "16px",
    background: "none",
    border: "none",
    fontSize: "1.5em",
    lineHeight: 1,
    color: theme.color.subtle,
    cursor: "pointer",
  },
  title: {
    margin: "0 0 8px",
    fontSize: "1.25em",
    color: theme.color.text,
  },
  description: {
    margin: "0 0 20px",
    fontSize: "0.9em",
    color: theme.color.muted,
    lineHeight: 1.5,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  textarea: {
    width: "100%",
    boxSizing: "border-box",
    padding: "10px 12px",
    borderRadius: theme.radius.md,
    border: `1px solid ${theme.color.borderStrong}`,
    fontFamily: theme.font.sans,
    fontSize: "0.95em",
    color: theme.color.text,
    resize: "vertical",
    outline: "none",
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    padding: "10px 12px",
    borderRadius: theme.radius.md,
    border: `1px solid ${theme.color.borderStrong}`,
    fontFamily: theme.font.sans,
    fontSize: "0.95em",
    color: theme.color.text,
    outline: "none",
  },
  send: {
    padding: "10px 16px",
    borderRadius: theme.radius.md,
    border: "none",
    background: theme.color.primary,
    color: "#fff",
    fontWeight: 600,
    fontSize: "0.95em",
    cursor: "pointer",
    boxShadow: theme.shadow.button,
  },
  sendDisabled: {
    background: theme.color.borderStrong,
    cursor: "not-allowed",
    boxShadow: "none",
  },
  error: {
    fontSize: "0.85em",
    color: theme.color.danger,
  },
};

export default FeedbackModal;
